import { httpStatusOf } from "utils/httpStatusOf";
import { reportError } from "utils/sentry/reportError";
/* eslint-disable @typescript-eslint/no-empty-function */

/**
 * Heartbeat / liveness contract (REF-6539, agreed with the BE):
 *
 * - The BE sends `{ type: "keep_alive" }` every KEEP_ALIVE_INTERVAL_MS while a socket is registered.
 *   `SocketContext` answers `{ type: "keepalive_ack" }`; any inbound text frame resets the BE's 600 s
 *   receive timeout. On that timeout the BE now closes with code 1000 / reason `idle_timeout`
 *   (previously it forgot the session but left the transport open — a "zombie" socket the browser
 *   still reported as OPEN, so no `onclose`, no reconnect, and every later completion frame was parked).
 * - FE liveness watchdog: once the first `keep_alive` has been seen on a connection (feature-detected, so
 *   an older BE that sends none causes no churn), a connection with no inbound frame of any kind for
 *   LIVENESS_TIMEOUT_MS is treated as dead and force-closed, which drives the normal reconnect path.
 * - Reconnect never gives up on transient failures: exponential backoff capped at MAX_RECONNECT_DELAY_MS
 *   with jitter, re-armed immediately on `online` / tab becoming visible / window focus, and every attempt
 *   asks the URL factory for a FRESH url (current token + signature). It stops only on an explicit
 *   `disconnect()`, when the factory returns null (no token → logged out), or on an application auth
 *   close code (4001/4003).
 * - Dead credentials: a rejected upgrade (HTTP 403 "token has expired") reaches the browser only as a
 *   1006 close, so the manager counts handshakes that never opened and hands that count to the factory
 *   (WsUrlContext). The factory (`context/wsUrlFactory`) asks the SERVER whether the session is still
 *   valid and returns null once it is not — this manager never decides auth on its own.
 */
export const KEEP_ALIVE_INTERVAL_MS = 60_000;
export const LIVENESS_TIMEOUT_MS = 3 * KEEP_ALIVE_INTERVAL_MS;
/**
 * Client heartbeat: `{ type: "ping" }` every 60 s while OPEN. Independent of the BE keep_alive — it is
 * what keeps an idle socket registered on a BE that predates REF-6539 (its 600 s receive timeout is reset
 * by ANY inbound text frame). The server keep_alive → ack pair is the BE's liveness signal; the client
 * ping is the FE's. Either alone keeps the 600 s timer from firing; both together also let each side
 * detect a half-open connection. 60 s stays under the ≥1/min timer throttle of a hidden tab.
 */
export const CLIENT_PING_INTERVAL_MS = 60_000;
/** A handshake that neither opens nor closes within this window is treated as failed. */
export const HANDSHAKE_TIMEOUT_MS = 20_000;
export const MAX_RECONNECT_DELAY_MS = 30_000;
/** After this many consecutive failures the cap widens (credentials may be dead; the url factory probes the session). */
export const SLOW_RETRY_AFTER_ATTEMPTS = 10;
export const SLOW_RECONNECT_DELAY_MS = 120_000;
export const BASE_RECONNECT_DELAY_MS = 1_000;
/** Close codes after which retrying with the same credentials is pointless. */
const AUTH_CLOSE_CODES = new Set([4001, 4003]);

/** What the manager knows when it asks the factory for a url. */
export interface WsUrlContext {
  /**
   * Consecutive sockets that closed WITHOUT ever opening, since the last successful open. A browser
   * reports a rejected upgrade (HTTP 401/403 — e.g. an expired token) only as a 1006 close, exactly like a
   * network failure, so this count is the only transport-level hint that the credentials may be dead.
   */
  failedHandshakes: number;
}

export type WsUrlFactory = (ctx: WsUrlContext) => Promise<string | null> | string | null;

class WebSocketManager {
  private static instance: WebSocketManager;
  private ws: WebSocket | null = null;
  private isConnecting = false;
  private listeners: Set<(data: any) => void> = new Set();
  private connectionStateListeners: Set<(connected: boolean) => void> = new Set();
  private reconnectTimer: NodeJS.Timeout | null = null;
  private reconnectAttempts = 0;
  /** See WsUrlContext.failedHandshakes. Unlike reconnectAttempts it is NOT reset by wakeup/liveness: a focus event does not revive a dead token. */
  private failedHandshakes = 0;
  private urlFactory: WsUrlFactory | null = null;
  private shouldReconnect = true;
  private sessionId: string | null = null;
  private livenessTimer: NodeJS.Timeout | null = null;
  private pingTimer: NodeJS.Timeout | null = null;
  private handshakeTimer: NodeJS.Timeout | null = null;
  private lastInboundAt = 0;
  private keepAliveSeen = false;
  private wakeListenersInstalled = false;
  /** Bumped on every connect() and disconnect(); a factory that resolves for an older attempt is dropped. */
  private connectAttempt = 0;

  private constructor() {
    this.installWakeListeners();
  }

  static getInstance(): WebSocketManager {
    if (!WebSocketManager.instance) {
      WebSocketManager.instance = new WebSocketManager();
    }

    return WebSocketManager.instance;
  }

  /**
   * Connect using a URL factory (preferred — re-evaluated on every reconnect so a rotated token is
   * picked up) or a fixed url string (legacy callers). `isReconnect` keeps the stored factory.
   */
  async connect(urlOrFactory: string | WsUrlFactory, isReconnect = false): Promise<void> {
    // Register the caller's factory FIRST so a later reconnect uses the newest credentials even when
    // this call itself is a no-op because a socket is already open.
    if (!isReconnect) {
      this.urlFactory = typeof urlOrFactory === "function" ? urlOrFactory : () => urlOrFactory;
      this.shouldReconnect = true;
    }

    if (this.ws?.readyState === WebSocket.OPEN || this.ws?.readyState === WebSocket.CONNECTING) {
      console.log("[WebSocketManager] Already connected or connecting");

      return;
    }

    if (this.isConnecting) {
      if (isReconnect) {
        console.log("[WebSocketManager] Connection already in progress");

        return;
      }
      // A NEWER explicit connect (fresh credentials) supersedes a factory evaluation still in flight:
      // bumping the attempt below makes the older evaluation drop its result.
      console.log("[WebSocketManager] Superseding in-flight connection attempt with newer credentials");
    }

    this.isConnecting = true;
    this.clearReconnectTimer();
    const attempt = ++this.connectAttempt;

    let url: string | null;
    try {
      url = typeof urlOrFactory === "function" ? await urlOrFactory({ failedHandshakes: this.failedHandshakes }) : urlOrFactory;
    } catch (error) {
      if (attempt === this.connectAttempt) this.isConnecting = false;
      throw error;
    }

    // Superseded while awaiting the factory (disconnect() or a newer connect()) — drop this attempt.
    if (attempt !== this.connectAttempt || !this.shouldReconnect) {
      console.log("[WebSocketManager] Connection attempt superseded, dropping");

      return;
    }

    if (!url) {
      // No credentials (logged out / token cleared) — nothing to retry against.
      this.isConnecting = false;
      console.log("[WebSocketManager] No url/credentials available, not connecting");

      return;
    }

    return new Promise((resolve, reject) => {
      try {
        if (this.ws) {
          this.ws.close();
          this.ws = null;
        }

        const socket = new WebSocket(url as string);
        this.ws = socket;
        this.armHandshakeTimeout(socket);
        let opened = false;
        let malformedReported = false;

        socket.onopen = () => {
          if (this.ws !== socket) return;
          opened = true;
          this.clearHandshakeTimeout();
          this.isConnecting = false;
          this.reconnectAttempts = 0;
          this.failedHandshakes = 0;
          this.keepAliveSeen = false;
          this.lastInboundAt = Date.now();
          this.startLivenessWatchdog();
          this.startClientPing();
          console.log("[WebSocketManager] Connected successfully");
          this.notifyConnectionState(true);
          resolve();
        };

        socket.onmessage = (event) => {
          if (this.ws !== socket) return;
          // Before the parse: any inbound frame, even one we cannot read, proves the socket is alive.
          this.lastInboundAt = Date.now();
          let data: any;
          try {
            data = JSON.parse(event.data);
          } catch {
            // REF-6871 (D1): a non-JSON frame is dropped instead of throwing out of the handler. Reported
            // once per socket, never with the frame or the SyntaxError (its message quotes the payload).
            if (!malformedReported) {
              malformedReported = true;
              reportError(new Error("WebSocket frame is not valid JSON"), {
                source: "websocket",
                featureArea: "unknown",
                fingerprint: ["ws-malformed-frame"],
              });
            }

            return;
          }
          if (data?.type === "keep_alive") this.keepAliveSeen = true;
          const messageWithTimestamp = { ...data, timestamp: Date.now() };
          this.notifyListeners(messageWithTimestamp);
        };

        socket.onclose = (event) => {
          // A stale socket (replaced by a newer connect) must not drive state or reconnects.
          if (this.ws !== socket) return;
          this.clearHandshakeTimeout();
          this.isConnecting = false;
          this.stopLivenessWatchdog();
          this.stopClientPing();
          console.log(`[WebSocketManager] Disconnected (code: ${event.code}, reason: ${event.reason || "No reason provided"})`);
          this.notifyConnectionState(false);
          this.ws = null;
          if (!opened) this.failedHandshakes++;

          if (AUTH_CLOSE_CODES.has(event.code)) {
            // Credentials rejected by the application layer: retrying with the same token is a storm.
            // The HTTP side (401 → logout) owns the auth flow; we stop here.
            console.error(`[WebSocketManager] Auth close code ${event.code} — not reconnecting`);
            this.shouldReconnect = false;

            return;
          }

          if (this.shouldReconnect) {
            this.scheduleReconnect();
          }
        };

        socket.onerror = (error) => {
          if (this.ws !== socket) return;
          this.isConnecting = false;
          // REF-6708: never the event itself — its target is the socket, whose url carries the HMAC auth query.
          console.log("[WebSocketManager] Error");
          reject(error);
        };
      } catch (error) {
        this.isConnecting = false;
        reject(error);
      }
    });
  }

  disconnect(): void {
    this.shouldReconnect = false;
    this.failedHandshakes = 0; // the next session (e.g. a fresh login) starts clean
    this.connectAttempt++; // invalidates any factory evaluation still in flight
    this.clearReconnectTimer();
    this.clearHandshakeTimeout();
    this.stopLivenessWatchdog();
    this.stopClientPing();
    if (this.ws) {
      const socket = this.ws;
      this.ws = null;
      socket.close();
      this.notifyConnectionState(false);
    }
    this.isConnecting = false;
  }

  send(data: any): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      try {
        this.ws.send(JSON.stringify(data));
      } catch (error) {
        console.log("[WebSocketManager] Failed to send message", { status: httpStatusOf(error) });
      }
    } else {
      console.log("[WebSocketManager] Cannot send message - not connected");
    }
  }

  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  addMessageListener(callback: (data: any) => void): () => void {
    this.listeners.add(callback);

    return () => this.listeners.delete(callback);
  }

  addConnectionStateListener(callback: (connected: boolean) => void): () => void {
    this.connectionStateListeners.add(callback);
    callback(this.isConnected());

    return () => this.connectionStateListeners.delete(callback);
  }

  /**
   * Called when the environment signals we may be able to connect again (network back, tab visible,
   * window focused). If we are disconnected and allowed to reconnect, skip the pending backoff and try now.
   */
  wakeup(): void {
    if (!this.shouldReconnect || !this.urlFactory) return;
    if (this.isConnected() || this.isConnecting || this.ws?.readyState === WebSocket.CONNECTING) return;
    console.log("[WebSocketManager] Wakeup — reconnecting now");
    this.clearReconnectTimer();
    this.reconnectAttempts = 0;
    this.attemptReconnect();
  }

  private notifyListeners(data: any): void {
    this.listeners.forEach((callback) => callback(data));
  }

  private notifyConnectionState(connected: boolean): void {
    this.connectionStateListeners.forEach((callback) => callback(connected));
  }

  /**
   * Exponential backoff with jitter, capped — never gives up on its own. After SLOW_RETRY_AFTER_ATTEMPTS
   * consecutive failures (a pre-accept auth rejection looks like a 1006 to the browser, so it cannot be
   * told apart from a network outage) the cadence drops to one attempt per SLOW_RECONNECT_DELAY_MS.
   */
  static computeReconnectDelay(attempt: number, random: () => number = Math.random): number {
    const cap = attempt >= SLOW_RETRY_AFTER_ATTEMPTS ? SLOW_RECONNECT_DELAY_MS : MAX_RECONNECT_DELAY_MS;
    const exp = Math.min(BASE_RECONNECT_DELAY_MS * Math.pow(2, attempt), cap);
    const jitter = Math.floor(random() * exp * 0.3);

    return Math.min(exp + jitter, cap);
  }

  private scheduleReconnect(): void {
    const delay = WebSocketManager.computeReconnectDelay(this.reconnectAttempts);
    this.reconnectAttempts++;

    console.log(`[WebSocketManager] Scheduling reconnection attempt ${this.reconnectAttempts} in ${delay}ms`);

    this.clearReconnectTimer();
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.attemptReconnect();
    }, delay);
  }

  private attemptReconnect(): void {
    if (!this.shouldReconnect || !this.urlFactory) return;
    const factory = this.urlFactory;
    const withSession: WsUrlFactory = async (ctx) => {
      const base = await factory(ctx);
      if (!base) return null;
      if (!this.sessionId) return base;
      const separator = base.includes("?") ? "&" : "?";

      return `${base}${separator}session_id=${this.sessionId}`;
    };

    console.log(`[WebSocketManager] Attempting to reconnect (attempt ${this.reconnectAttempts})`);
    this.connect(withSession, true).catch((error) => {
      console.log("[WebSocketManager] Reconnection attempt failed", { status: httpStatusOf(error) });
      // `onclose` schedules the next attempt. If the handshake failed without ever producing a socket
      // close (factory threw / constructor threw), schedule it here so the loop cannot stall.
      if (!this.ws && this.shouldReconnect && !this.reconnectTimer) {
        this.scheduleReconnect();
      }
    });
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  // ---- client heartbeat + handshake deadline ------------------------------------------------

  private startClientPing(): void {
    this.stopClientPing();
    this.pingTimer = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) this.send({ type: "ping" });
    }, CLIENT_PING_INTERVAL_MS);
  }

  private stopClientPing(): void {
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  private armHandshakeTimeout(socket: WebSocket): void {
    this.clearHandshakeTimeout();
    this.handshakeTimer = setTimeout(() => {
      this.handshakeTimer = null;
      if (this.ws !== socket || socket.readyState !== WebSocket.CONNECTING) return;
      console.log("[WebSocketManager] Handshake timed out — retrying");
      this.ws = null;
      this.isConnecting = false;
      try {
        socket.close();
      } catch {
        // ignore
      }
      if (this.shouldReconnect) this.scheduleReconnect();
    }, HANDSHAKE_TIMEOUT_MS);
  }

  private clearHandshakeTimeout(): void {
    if (this.handshakeTimer) {
      clearTimeout(this.handshakeTimer);
      this.handshakeTimer = null;
    }
  }

  // ---- liveness watchdog -------------------------------------------------------------------

  private startLivenessWatchdog(): void {
    this.stopLivenessWatchdog();
    this.livenessTimer = setInterval(() => this.checkLiveness(), KEEP_ALIVE_INTERVAL_MS);
  }

  private stopLivenessWatchdog(): void {
    if (this.livenessTimer) {
      clearInterval(this.livenessTimer);
      this.livenessTimer = null;
    }
  }

  private checkLiveness(): void {
    if (!this.keepAliveSeen || !this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    const silentFor = Date.now() - this.lastInboundAt;
    if (silentFor < LIVENESS_TIMEOUT_MS) return;
    console.log(`[WebSocketManager] No inbound frame for ${silentFor}ms — treating socket as dead, reconnecting`);
    // `close()` drives `onclose` → scheduleReconnect. Some browsers delay the close event on a dead
    // TCP flow; drop our reference and reconnect explicitly so we do not wait on it.
    const dead = this.ws;
    this.ws = null;
    this.stopLivenessWatchdog();
    this.stopClientPing();
    this.notifyConnectionState(false);
    try {
      dead.close(4000, "liveness_timeout");
    } catch {
      // ignore — the socket is already unusable
    }
    this.reconnectAttempts = 0;
    this.attemptReconnect();
  }

  // ---- wake signals ------------------------------------------------------------------------

  private installWakeListeners(): void {
    if (this.wakeListenersInstalled || typeof window === "undefined") return;
    this.wakeListenersInstalled = true;
    window.addEventListener("online", () => this.wakeup());
    window.addEventListener("focus", () => this.wakeup());
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") this.wakeup();
    });
  }

  // ---- legacy manual controls ---------------------------------------------------------------

  /**
   * Also the revive path main shipped for REF-6509 ("notifications arriving late, all at once" after a
   * laptop lid close spent the old 10-attempt budget). Superseded here by `wakeup()` — same three
   * browser events, same reset of the attempt budget, and an IMMEDIATE attempt rather than a backoff
   * wait in front of a user who is already looking.
   */
  enableReconnection(): void {
    this.shouldReconnect = true;
    this.reconnectAttempts = 0;
    if (!this.isConnected() && this.urlFactory) {
      this.clearReconnectTimer();
      this.attemptReconnect();
    }
  }

  disableReconnection(): void {
    this.shouldReconnect = false;
    this.clearReconnectTimer();
  }

  setSessionId(sessionId: string): void {
    this.sessionId = sessionId;

    if (process.env.NODE_ENV === "development") {
      console.log("[WebSocketManager] Session ID set:", sessionId);
    }
  }

  getSessionId(): string | null {
    return this.sessionId;
  }
}

export { WebSocketManager };
export default WebSocketManager.getInstance();
